<template>
    <div class="bg-grey-lightest">
        <section id="heading" class="pt-12 pb-24">
            <!-- Header Nav -->
            <header-nav ref="headerNav"></header-nav>
        </section>
        <section class="py-12">
            <div class="max-w-sm mx-auto px-6">
                <div class="rounded bg-white shadow relative -mt-24">
                    <form @submit.prevent="submitPatientForm" v-if="step == 'patientForm'">
                        <router-link :to="{ path: '/' }" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-arrow-left"></i></span>
                        </router-link>
                        <div class="p-6 pt-12">
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Firstname_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="first_name"
                                    v-model="patientForm.firstName"
                                    v-validate="'required'"
                                    :class="{'border-red': errors.has('first_name') }"
                                    type="text">
                                <span v-show="errors.has('first_name')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_first_name') }}</span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Lastname_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="last_name"
                                    v-model="patientForm.lastName"
                                    v-validate="'required'"
                                    :class="{'border-red': errors.has('last_name') }"
                                    type="text">
                                <span v-show="errors.has('last_name')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_last_name') }}</span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Age_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="patientForm.age"
                                    v-model="patientForm.age"
                                    v-validate="'required|numeric'"
                                    :class="{'border-red': errors.has('patientForm.age') }"
                                    type="number" step="1" min="0">
                                <span v-show="errors.has('patientForm.age')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_age') }}</span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Medical_practice_') }}</label>
                                <div class="relative">
                                    <select class="appearance-none bg-grey-lightest border pl-3 pr-8 py-2 text-grey-darker rounded w-full block outline-0" v-model="patientForm.type" required>
                                        <option :value="null" disabled>{{ $t('Select_a_medical_practice') }}</option>
                                        <option v-for="type in types" :key="type" :value="type">{{ $t('type_' + type) }}</option>
                                    </select>
                                    <div class="pointer-events-none absolute pin-t pt-3 pin-r flex items-center px-2 text-grey-darker">
                                        <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                    </div>
                                </div>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Locality_') }}</label>
                                 <gmap-autocomplete
                                    @place_changed="setPatientFormLocality"
                                    :selectFirstOnEnter="true"
                                    :options="autocompleteOptions"
                                    :value="patientForm.locality"
                                    :placeholder="patientForm.locality ? patientForm.locality : $t('Enter_a_location')"
                                    id="search-form-field"
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    :class="{'border-red': errors.has('locality') }"
                                    ></gmap-autocomplete>
                                <span v-show="errors.has('locality')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_locality') }}</span>
                            </div>
                            <div class="mb-4" v-if="!patient || patient.email === null">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Email_address_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    name="email"
                                    v-model="patientForm.email"
                                    v-validate="'required|email'"
                                    :class="{'border-red': errors.has('email') }"
                                    type="email">
                                <span v-show="errors.has('email')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_email_address') }}</span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Phone_number_') }}</label>
                                <input
                                    class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                    @blur="transformPhone"
                                    name="phone"
                                    v-model="patientForm.phone"
                                    v-validate="'required|phone:BE'"
                                    :class="{'border-red': errors.has('phone') }"
                                    type="phone">
                                <span v-show="errors.has('phone')" class="text-xs text-red font-semibold">{{ $t('Please_fill_your_phone_number') }}</span>
                            </div>
                            <button type="submit" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Next') }}</button>
                        </div>
                    </form>
                    <div v-if="step == 'patientSymptoms'">
                        <button type="button" @click="changeStep('patientForm')" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-arrow-left"></i></span>
                        </button>
                        <div class="px-6 pt-12 pb-4" v-if="sections.length > 0">
                            <form @submit.prevent="nextSection" class="mb-4" v-for="(section, sectionIndex) in sections" :key="`section-${sectionIndex}`" v-show="sectionIndex === currentSection" :data-vv-scope="`section-${sectionIndex}`">
                                <div class="mb-4 text-lg font-semibold">{{ section.title }}</div>
                                <div class="text-sm text-grey-dark mb-4" v-if="section.description" v-html="section.description"></div>

                                <div
                                    class="mb-4"
                                    v-for="(question, questionIndex) in section.questions"
                                    :key="`question-${questionIndex}`"
                                    v-if="!Object.keys(question).includes('conditions') || conditionsMatches(question, section)"
                                >
                                    <div class="text-blue text-sm font-semibold mb-4">{{ question.label }}</div>
                                    <template v-if="question.type === 'checkboxes'">
                                        <label v-for="(choice, choiceIndex) in question.choices" :key="`choice-${choiceIndex}`" class="relative flex items-center text-sm text-grey-dark group-hover:text-grey-darkest mb-2">
                                            <input
                                                type="checkbox"
                                                class="mr-2"
                                                @input="toggleSymptom(question, choice)"
                                                :value="choice"
                                                :checked="question.value.length && question.value.includes(choice)"
                                                :name="`section.${sectionIndex}.${questionIndex}`"
                                                v-validate="'required'">
                                            <span>{{ choice }}</span>
                                        </label>
                                        <span v-show="errors.has(`section.${sectionIndex}.${questionIndex}`)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                                    </template>
                                    <template v-if="question.type === 'select'">
                                        <select
                                            class="appearance-none bg-grey-lightest border pl-3 pr-8 py-2 text-grey-darker rounded w-full block outline-0"
                                            v-model="question.value"
                                            :name="`section.${sectionIndex}.${questionIndex}`"
                                            v-validate="'required'">
                                            <option :value="null">{{ $t('Select_an_option') }}</option>
                                            <option v-for="(choice, choiceIndex) in question.choices" :key="`choice-${choiceIndex}`" :value="choice">{{ choice }}</option>
                                        </select>
                                        <span v-show="errors.has(`section.${sectionIndex}.${questionIndex}`)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                                    </template>
                                    <template v-if="question.type === 'radio'">
                                        <label v-for="(choice, choiceIndex) in question.choices" :key="`choice-${choiceIndex}`" class="relative flex items-center text-sm text-grey-dark group-hover:text-grey-darkest mb-3">
                                            <input
                                                type="radio"
                                                class="mr-2"
                                                v-model="question.value"
                                                :value="choice"
                                                :name="`section.${sectionIndex}.${questionIndex}`"
                                                v-validate="'required'">
                                            <span>{{ choice }}</span>
                                        </label>
                                        <span v-show="errors.has(`section.${sectionIndex}.${questionIndex}`)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                                    </template>
                                    <template v-if="question.type === 'boolean'">
                                        <div class="flex flex-wrap -mx-4">
                                            <label class="block group mb-2 px-4">
                                                <input
                                                    type="radio"
                                                    class="mr-2"
                                                    :value="true"
                                                    v-model="question.value"
                                                    :name="`section.${sectionIndex}.${questionIndex}`"
                                                    v-validate="'required'">
                                                <span class="text-sm text-grey-dark group-hover:text-grey-darkest">Yes</span>
                                            </label>
                                            <label class="block group mb-2 px-4">
                                                <input
                                                    type="radio"
                                                    class="mr-2"
                                                    :value="false"
                                                    v-model="question.value"
                                                    :name="`section.${sectionIndex}.${questionIndex}`"
                                                    v-validate="'required'">
                                                <span class="text-sm text-grey-dark group-hover:text-grey-darkest">No</span>
                                            </label>
                                        </div>
                                        <span v-show="errors.has(`section.${sectionIndex}.${questionIndex}`)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                                    </template>
                                    <template v-if="question.type === 'textarea'">
                                        <textarea
                                            class="block w-full rounded border bg-grey-lightest px-3 py-2 text-grey-darkest"
                                            rows="5"
                                            v-model="question.value"
                                            :name="`section.${sectionIndex}.${questionIndex}`"
                                            v-validate="'required'"></textarea>
                                        <span v-show="errors.has(`section.${sectionIndex}.${questionIndex}`)" class="text-xs text-red font-semibold">{{ $t('Please_specify') }}</span>
                                    </template>
                                </div>

                                <button @click="nextSection" type="button" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3">{{ $t('Next') }}</button>
                                <button v-if="currentSection > 0" @click="previousSection" type="button" class="bg-transparent hover:underline text-sm text-grey-darker w-full px-4 py-3 mt-2">{{ $t('Go_back') }}</button>
                            </form>
                        </div>
                    </div>
                    <div v-if="step == 'submissionSummary'">
                        <button @click="changeStep('patientSymptoms')" class="text-grey hover:text-black text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-arrow-left"></i></span>
                        </button>
                        <div class="px-6 py-4">
                            <h4 class="text-xl uppercase tracking-wide text-grey-darkest">{{ $t('Summary') }}</h4>
                        </div>
                        <div class="p-6 pt-4">
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Name') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.firstName + ' ' + patientForm.lastName"></span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Age') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.age"></span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Locality') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.locality"></span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Email_address') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.email"></span>
                            </div>
                            <div class="mb-4">
                                <label class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ $t('Phone_number') }}</label>
                                <span class="block w-full text-grey-darker" v-text="patientForm.phone"></span>
                            </div>

                            <div v-for="(section, sectionIndex) in sections" :key="sectionIndex">
                                <div class="mb-4 text-lg font-semibold">{{ section.title }}</div>
                                <div
                                    class="mb-4"
                                    v-for="(question, questionIndex) in section.questions"
                                    :key="`question-${questionIndex}`"
                                >
                                    <div class="block mb-1 uppercase tracking-wide text-sm font-semibold text-grey-dark">{{ question.label }}</div>
                                    <template v-if="typeof question.value === 'boolean'">
                                        <span class="block w-full text-grey-darker">{{ question.value ? 'Yes' : 'No' }}</span>
                                    </template>
                                    <template v-else-if="Array.isArray(question.value)">
                                        <span class="block w-full text-grey-darker">{{ question.value.join(', ') }}</span>
                                    </template>
                                    <template v-else>
                                        <span class="block w-full text-grey-darker">{{ question.value }}</span>
                                    </template>
                                </div>
                            </div>
                            <div class="relative">
                                <bubble>{{ $t('I_will_inform_you_as_soon_as_a_timeslot_is_available') }}</bubble>
                                <button @click="confirmReservation" class="bg-blue hover:bg-blue-dark rounded text-sm text-white font-semibold uppercase tracking-wide w-full px-4 py-3 mt-6">{{ $t('Confirm') }}</button>
                            </div>
                        </div>
                    </div>
                    <div v-if="step == 'submitting'">
                        <div class="flex items-center justify-center h-64">
                            <div class="text-center">
                                <span class="icon text-5xl text-grey"><i class="mdi mdi-loading mdi-spin"></i></span><br />
                                <span class="text-sm text-grey-darker mt-4">{{ $t('Loading') }}</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="step == 'submitted'">
                        <router-link :to="{ path: '/' }" class="text-green-lighter hover:text-green-lightest text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-close"></i></span>
                        </router-link>
                        <div class="flex items-center justify-center bg-green text-white rounded px-4 py-10 w-full">
                            <span class="icon text-5xl text-white"><i class="mdi mdi-verified"></i></span>
                            <span class="text-sm text-xl ml-4">{{ $t('As_soon_as_a_appointment_is_available_Tood_will_inform_you') }}</span>
                        </div>
                    </div>
                    <div v-if="step == 'submissionError'">
                        <router-link :to="{ path: '/' }" class="text-red-lighter hover:text-red-lightest text-xl absolute pin-t pin-r p-2 m-2">
                            <span class="icon"><i class="mdi mdi-close"></i></span>
                        </router-link>
                        <div class="flex items-center justify-center bg-red text-white rounded px-4 py-10 w-full">
                            <span class="icon text-5xl text-white"><i class="mdi mdi-alert-circle"></i></span>
                            <span class="text-sm text-xl ml-4" v-text="errorText ? errorText : $t('an_error_occured')"></span>
                        </div>
                    </div>
                </div>
                <div class="mt-3 text-sm text-center text-grey-dark" v-if="step == 'submitted'">{{ $t('Redirecting') }}</div>
            </div>
        </section>
    </div>
</template>

<script>
import firebase from 'firebase/app'
import axios from 'axios'
import helpers from '@/mixins/helpers'
import PhoneNumber from 'awesome-phonenumber'

import Bubble from '@/components/Patient/Bubble'
import HeaderNav from '@/components/Patient/HeaderNav'

import emergencyRepository from '@/data/EmergencyRepository'

export default {
    name: 'EmergencyNew',

    mixins: [helpers],

    components: {Bubble, HeaderNav},

    data () {
        return {
            step: 'patientForm',
            isBooked: false,
            emergency: null,
            questions: [],
            sections: [],
            symptoms: {},
            currentSection: 0,
            errorText: false,
            autocompleteOptions: {
                types: ['(regions)'],
                componentRestrictions: { country: 'be' }
            },
            patientForm: {
                lastName: '',
                firstName: '',
                email: '',
                id: '',
                phone: '',
                age: '',
                locality: '',
                zipCode: null,
                location: '',
                type: null
            },
            types: [
                'DENTIST',
                'GENERAL_PRACTITIONER',
                'GYNECOLOGIST',
                'OPHTHALMOLOGIST',
                'PEDIATRICIAN',
                'PSYCHOLOGIST',
                'DERMATOLOGIST',
                'OSTEOPATH',
                'PSYCHIATRIST',
                'PHYSIOTHERAPIST',
            ]
        }
    },

    mounted () {
        let firebaseUser = firebase.auth().currentUser

        if (firebaseUser) {
            this.prefillFromFirebase()
        } else {
            this.showLogin()
        }

        if (this.patientMedicalPractice) {
            this.patientForm.type = this.patientMedicalPractice
        } else if (this.$route.query.type) {
            this.patientForm.type = this.$route.query.type
        }

        this.populateSymptoms()
    },

    methods: {
        showLogin () {
            this.$refs.headerNav.showLogin()
        },

        async changeStep (step) {
            if (step != 'submissionError') {
                this.errorText = false
            }

            if (step == 'patientSymptoms') {
                // Make sure this gets loaded after the translation is done
                await this.populateSymptoms()
            }

            this.step = step
            window.scrollTo(0, 0)
        },

        submitPatientForm () {
            this.$validator.validateAll().then((result) => {
                if (! this.patientForm.locality || ! this.patientForm.location) {
                    this.errors.add({ field: 'locality', msg: 'Locality ou Location is missing.' });
                } else if (result) {
                    this.changeStep('patientSymptoms')
                }
            })
        },

        submitPatientSymptoms () {
            this.changeStep('submissionSummary')
        },

        confirmReservation () {
            this.changeStep('submitting')

            let patient = {
                id: this.patientForm.id,
                firstName: this.hashPatientInfo(this.patientForm.firstName),
                lastName: this.hashPatientInfo(this.patientForm.lastName),
                age: parseInt(this.patientForm.age),
                email: this.hashPatientInfo(this.patientForm.email),
                phone: this.hashPatientInfo(this.patientForm.phone),
                zipCode: this.patientForm.zipCode,
                locality: this.patientForm.locality,
                location: this.patientForm.location,
                symptoms: this.sections,
                type: this.patientForm.type
            }

            emergencyRepository.create(patient, () => {
                this.changeStep('submitted')

                this.$ga.event({
                    eventCategory: 'Emergencies',
                    eventAction: 'CreateEmergency'
                })

                setTimeout(() => {
                    this.$router.push('/')
                }, 3000)
            }, (error) => {
                this.errorText = error.message
                this.changeStep('submissionError')
            })
        },

        prefillFromFirebase () {
            let firebaseUser = firebase.auth().currentUser

            if (firebaseUser) {
                this.patientForm.id = firebaseUser.uid

                this.patientForm.email = firebaseUser.email
                this.patientForm.phone = firebaseUser.phoneNumber

                let splittedName = firebaseUser.displayName.split(" ")
                this.patientForm.firstName = splittedName[0]
                delete splittedName[0]
                this.patientForm.lastName = (splittedName.join(" ")).trim()

                this.patientForm.locality = this.patientPlaceName ? this.patientPlace : ''
                this.patientForm.location = this.patientPosition ? new firebase.firestore.GeoPoint(this.patientPosition._lat, this.patientPosition._long) : null
            }
        },

        getElementInLanguage(element, language = null) {
            if (typeof element !== 'object') {
                return null
            }

            if (language === null) {
                language = this.$store.state.frontLanguage
            }

            if (Object.keys(element).includes(language)) {
                return element[language]
            }

            if (language === 'en') {
                return element[Object.keys(element)[0]]
            }

            return this.getElementInLanguage(element, 'en')
        },

        async populateSymptoms () {
            const { data } = await axios.get(process.env.API_BASE_URL + '/app/questions', {
                params: {
                    type: this.patientForm.type
                }
            })

            let splitInSections = false
            this.sections = []
            this.currentSection = 0

            data.map(question => {
                if (question.type === 'title') {
                    splitInSections = true

                    let section = {
                        title: this.getElementInLanguage(question.label),
                        description: this.getElementInLanguage(question.description),
                        questions: question.questions.map(q => {
                            let mappedQ = q
                            if (Object.keys(mappedQ).includes('choices')) {
                                mappedQ.choices = mappedQ.choices.map(choice => this.getElementInLanguage(choice))
                            }

                            mappedQ.label = this.getElementInLanguage(mappedQ.label)
                            if (mappedQ.type === 'boolean') {
                                mappedQ.value = false
                            } else if (mappedQ.type === 'checkboxes') {
                                mappedQ.value = []
                            } else {
                                mappedQ.value = ''
                            }

                            return mappedQ
                        })
                    }

                    this.sections.push(section)
                } else {
                    this.sections.push({
                        ...question,
                        value: []
                    })
                }
            })
        },

        transformPhone () {
            if (this.patientForm.phone) {
                let phoneNumber = new PhoneNumber(this.patientForm.phone, "BE")

                if (phoneNumber.isValid()) {
                    this.patientForm.phone = phoneNumber.getNumber( 'international' );
                }
            }
        },

        setPatientFormLocality (place) {
            if (place.geometry) {
                this.patientForm.locality = place.formatted_address
                this.patientForm.location = new firebase.firestore.GeoPoint(place.geometry.location.lat(), place.geometry.location.lng())
            } else {
                this.patientForm.locality = ''
                this.patientForm.location = null
            }
        },

        nextSection () {
            this.$validator.validateAll(`section-${this.currentSection}`).then((result) => {
                if (result) {
                    if (this.currentSection >= this.sections.length - 1) {
                        this.submitPatientSymptoms()
                    } else {
                        this.currentSection++
                        window.scrollTo(0, 0)
                    }
                }
            })


        },

        previousSection () {
            if (this.currentSection > 0) {
                this.currentSection--
                window.scrollTo(0, 0)
            }
        },

        toggleSymptom (question, symptom) {
            if (question.value.length && question.value.includes(symptom)) {
                question.value = question.value.filter(val => val !== symptom)
            } else {
                if (question.value.length) {
                    question.value.push(symptom)
                } else {
                    question.value = [symptom]
                }
            }
        },

        conditionsMatches (question, section) {
            let match = true

            question.conditions.forEach(condition => {
                let conditionQuestion = section.questions[condition.question]
                if (condition.comparison === '=') {
                    if (conditionQuestion.value !== condition.value) {
                        match = false
                    }
                }
            })

            return match
        }
    },

    watch: {
        'patientForm.locality' (newValue, oldValue) {
            this.errors.remove('locality')
        },
        '$store.state.frontLanguage' () {
            this.populateSymptoms()
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/Patient/EmergencyNew.vue